import { Overlay } from '@angular/cdk/overlay';
import { Component, EventEmitter, OnInit, Output, ViewContainerRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { AdvancedDataSource } from 'src/app/controls/advanced-gridview/datasources/advanced-datasource';
import { AdvancedGridviewModel } from 'src/app/controls/advanced-gridview/models/advanced-gridview-model';
import { ColumnDefinition } from 'src/app/controls/advanced-gridview/models/column-definition';
import { ColumnListDefinition } from 'src/app/controls/advanced-gridview/models/column-list-definition';
import { ColumnType } from 'src/app/controls/advanced-gridview/models/column-type';
import { MenuITemAction } from 'src/app/controls/advanced-gridview/models/menu-item-action';
import { MenuItemEvent } from 'src/app/controls/advanced-gridview/models/menu-item-event';
import { SortInfos } from 'src/app/controls/advanced-gridview/models/sort-infos';
import { ConfirmDialogComponent } from 'src/app/controls/confirm-dialog/confirm-dialog.component';
import { FilterType } from 'src/app/controls/filters/models/filter-type';
import { ListItem } from 'src/app/controls/filters/models/list-item';
import { DefaultListComponent } from 'src/app/controls/list/default-list/default-list.component';
import { EnumHelper } from 'src/app/helpers/enum-helper';
import { Rights } from 'src/app/models/enums/rights';
import { ViewType } from 'src/app/models/enums/view-type';
import { View } from 'src/app/models/view';
import { MemoryService } from 'src/app/services/memory.service';
import { NotificationService } from 'src/app/services/notification-service';
import { TaskProgressionService } from 'src/app/services/task-progression.service';
import { ViewsService } from 'src/app/services/views.service';
import { AdvancedGridviewComponent } from '../../../controls/advanced-gridview/advanced-gridview.component';

@Component({
    selector: 'app-views-list',
    templateUrl: './views-list.component.html',
    styleUrls: ['./views-list.component.scss'],
    imports: [AdvancedGridviewComponent]
})
export class ViewsListComponent extends DefaultListComponent<View> implements OnInit {
    /**
     * Configuration des colonnes de la liste.
     */
    tableConfig: ColumnDefinition[] = null;

    /**
     * Emis lorsque l'utilisateur clique sur le menu contextuel 'Activer l'archivage' d'une vue.
     */
    @Output()
    archivingActivated: EventEmitter<View> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur clique sur le menu contextuel 'Cloner' d'une vue.
     */
    @Output()
    cloned: EventEmitter<View> = new EventEmitter();

    /**
     * Constructeur
     * @param dialog : Service pour ouvrir des boites de dialogue.
     * @param memoryService : Service pour récupérer les informations en mémoire.
     * @param enumHelper : Classe utilitaire pour les énumérations.
     * @param viewsService : Service de gestion des vues.
     */
    constructor(
        public override dialog: MatDialog,
        protected override memoryService: MemoryService,
        public enumHelper: EnumHelper,
        private viewsService: ViewsService,
        private spinner: NgxSpinnerService,
        public overlay: Overlay,
        public viewContainerRef: ViewContainerRef,
        private taskProgressionService: TaskProgressionService,
        private notificationService: NotificationService
    ) {
        super(dialog, memoryService);

        // Specifie des colonnes en fonction des droits utilisateurs
        if (this.memoryService.getConnectedUser().params.idVisible === true) {
            this.tableConfig = [
                new ColumnDefinition('id', 'Id', ColumnType.Numeric, FilterType.Text, 1, 5, true),
                new ColumnDefinition('name', 'Nom', ColumnType.Text, FilterType.Text, 2, 60, true),
                new ColumnListDefinition('viewType', 'Type', FilterType.List, 3, 20, true, false),
                new ColumnDefinition('archivingEnabled', 'Archivage', ColumnType.Boolean, FilterType.Boolean, 4, 10, true),
            ];
        } else {
            this.tableConfig = [
                new ColumnDefinition('name', 'Nom', ColumnType.Text, FilterType.Text, 2, 65, true),
                new ColumnListDefinition('viewType', 'Type', FilterType.List, 3, 20, true, false),
                new ColumnDefinition('archivingEnabled', 'Archivage', ColumnType.Boolean, FilterType.Boolean, 4, 10, true),
            ];
        }

        this.AddMenuItem('Dupliquer', MenuITemAction.Duplicate, Rights.ViewCreate, this.isNotArchiveView);
        this.AddMenuItem('Éditer', MenuITemAction.Update, Rights.ViewEdit);
        this.AddMenuItem('Supprimer', MenuITemAction.Delete, Rights.ViewDelete, this.isNotArchiveView);
        this.AddMenuItem("Activer l'archivage", MenuITemAction.ActivateArchiving, Rights.Archiving, this.isArchivingAuthorized);
        this.AddMenuItem('Cloner', MenuITemAction.Clone, Rights.ViewCreate, this.isNotArchiveView);
    }

    /**
     * Initialise le composant.
     */
    ngOnInit(): void {
        this.dataSource = new AdvancedDataSource(null, this.viewsService, this.spinner, this.notificationService);
        this.filteredTableModel = new AdvancedGridviewModel(this.tableConfig, new SortInfos('name', 'asc'));

        // Ajout des listes déroulantes
        // attente de 500ms pour que la gridView ait le temps de se remplir.
        setTimeout(() => {
            this.loadListColumnFilterDatas();
        }, 500);
    }

    /**
     * Appellé lorsque l'utilisateur clique sur un élément du menu contextuel.
     * @param event : Événement retourné par le menu contextuel.
     */
    public onAction(event: MenuItemEvent<View>): void {
        if (event.action == MenuITemAction.Read) {
            // Cas de la consultation.
            this.itemDisplayed.emit(event.context);
        } else if (event.action == MenuITemAction.Update) {
            // Cas de la mise à jour.
            this.itemEdited.emit(event.context);
        } else if (event.action == MenuITemAction.Duplicate) {
            // Cas de la mise à jour.
            this.itemDuplicated.emit(event.context);
        } else if (event.action == MenuITemAction.Delete) {
            // Cas de la suppression --> affichage de la boite de dialogue de confirmation.
            this.displayDeleteConfirmationDialog(`Vous êtes sur le point de supprimer la vue '${event.context.name}'.`, event.context);
        } else if (event.action == MenuITemAction.ActivateArchiving) {
            // Cas de l'activation de l'archivage --> affichage de la boite de dialogue de confirmation.
            this.displayArchivingActivationConfirmationDialog(`Vous êtes sur le point d'activer l'archivage de la vue '${event.context.name}'.`, event.context);
        } else if (event.action == MenuITemAction.Clone) {
            // Cas du clonage.
            this.cloned.emit(event.context);
        }
    }

    /**
     * Charge les éléments des filtres des colonnes de type liste.
     */
    private loadListColumnFilterDatas(): void {
        var listItem: ListItem<string>[] = [];

        listItem.push(new ListItem('1', 'Bannettes'));
        listItem.push(new ListItem('2', 'Workflow'));
        listItem.push(new ListItem('3', 'Bannettes (archivée)'));
        listItem.push(new ListItem('4', 'Workflow (archivée)'));

        this.loadColumn(
            this.filteredTableModel.displayedColumnsDefinitions.find(
                (x) => this.enumHelper.areEquals(ColumnType, x.type, ColumnType.List) && x.columnName === 'viewType'
            ) as ColumnListDefinition<string>,
            listItem
        );
    }

    private isArchivingAuthorized: (item: View) => boolean = (item: View) => {
        return (
            this.memoryService.getSelectedOrganization().archivingEnabled === true &&
            item.archivingEnabled == false &&
            item.viewType !== ViewType.ArchivingBox &&
            item.viewType !== ViewType.ArchivingWorkflow
        );
    };

    private isNotArchiveView: (item: View) => boolean = (item: View) => {
        return item.viewType !== ViewType.ArchivingBox && item.viewType !== ViewType.ArchivingWorkflow;
    };

    /**
     * Affiche la boite de dialogue de confirmation de l'activation de l'archivage.
     * @param confirmationText : Texte de confirmation.
     * @param view : Instance de la vue pour laquelle activer l'archive.
     */
    private displayArchivingActivationConfirmationDialog(confirmationText: string, view: View) {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            maxWidth: '400px',
            data: {
                title: "Confirmation d'activation de l'archivage",
                message: confirmationText,
            },
            panelClass: 'empty-dialog',
        });

        // Attente de la réponse de la boite de dialogue de confirmation
        dialogRef.afterClosed().subscribe((dialogResult) => {
            if (dialogResult) {
                // Activation de l'archivage.
                this.archivingActivated.emit(view);
            }
        });
    }
}
